import React, { useRef, useContext, useCallback, useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import NotificationManager from '../NotificationManager';

import {
  getOrCreateGlobalContext,
  CONTEXT_NAMES
} from '@adsk/bim360-matrix-react-helpers/es/globalContexts';

const NotificationContext = getOrCreateGlobalContext(CONTEXT_NAMES.NotificationContext);

const ACTIONS = {
  SHOW_NOTIFICATION: 'SHOW_NOTIFICATION',
  RESET_NOTIFICATION: 'RESET_NOTIFICATION'
};

const initialState = null;

const notificationManagerReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SHOW_NOTIFICATION:
      return action.payload;
    case ACTIONS.RESET_NOTIFICATION:
      return initialState;
    default:
      console.error(`NotificationProvider - Unknown action type: ${action.type}`);
      return initialState;
  }
};

/**

 Context based usage for `NotificationManager`, for handling notifications.
 Add `NotificationProvider` somewhere up React components tree.
 Calling `showNotification` function will show a notification.

 ```
 const { showNotification } = useNotification();

 showNotification({
    level: NotificationLevels.SUCCESS,
    type: NotificationTypes.TOAST,
    message: 'My message!'
  })
 ```
 */
export const NotificationProvider = ({ children }) => {
  const notificationManagerRef = useRef(null);
  const [notification, dispatch] = useReducer(notificationManagerReducer, initialState);
  const showNotification = useCallback(
    notification => dispatch({ type: ACTIONS.SHOW_NOTIFICATION, payload: notification }),
    []
  );

  useEffect(() => {
    if (notification) {
      notificationManagerRef.current.addNotification(notification);

      // Resetting the notification in the store so that components that rely on the the notification in the store will be updated and won't "remember" an unrelated notification.
      dispatch({ type: ACTIONS.RESET_NOTIFICATION });
    }
  }, [notification]);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      <NotificationManager ref={notificationManagerRef} />
      {children}
    </NotificationContext.Provider>
  );
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
